// The bear's game frame (sprint 35 item 3), drawn only from the Aseprite exports in client/art/aseprite/bear-game/
// (art/aseprite/src/bear_game.lua). This file chooses which frame of which sheet goes where on each 1/15 s step, and cuts
// tiles to the edge of the area they fill; it draws no shape of its own. bearScene() is pure (tested in
// test/bear-game-art.test.js); drawBear() copies its list onto the canvas.
//
// Reading the game's observation (game/minigames/bear.js observe()):
//   { t, box: {w, h}, half: {w, h}, me: {x, y}, V, threats: [{ warnStep, slamStep, rects: [{x0, x1, y0, y1}] }],
//     marks?: 3..0 (marks left; 3 if absent), hitStep?: the step the last mark was lost (-1 or absent: none), tell?: eyes }
//  - a warning (warnStep <= t < slamStep): the 25 % Bayer shadow over exactly each rect, 50 % for its last 3 steps; the paw
//    hangs raised over the title bar at the rect (higher until those last 3 steps); the bear lifts that arm and narrows its eyes;
//  - solid (slamStep .. slamStep + 2): the paw comes down through the title bar (tearing it) and covers exactly each rect:
//    the forearm tiled above, the paw's toes and claws along the bottom (a rake when the rect stops short of the floor);
//  - after: the paw lifts (2 steps), claw marks fade on the floor, the tear stays;
//  - a lost mark: one of the three slots in the title bar is scratched through; the doorkeeper flashes for 6 steps of mercy
//    (stepped frames; under reduced motion the flash holds still) and the bear roars;
//  - the end (t >= 105 with a mark left): the bear slumps and yawns, the doorkeeper throws his arms up, "STILL HERE!" stamps in.
//    Out (no marks left): the bear roars, the doorkeeper lies flat. The page's sentence line says the rest.
// Reduced motion: no flicker; everything else is already a stepped frame.
import { sheet, blit, blitPart, frameOf } from './minigame-art.js';

const PIECE = 'bear-game';
export const ART = {
  night: sheet(PIECE, 'bear-night', { still: true }),
  bear: sheet(PIECE, 'bear-bear'),
  eyes: sheet(PIECE, 'bear-eyes'),
  window: sheet(PIECE, 'bear-window', { still: true }),
  tear: sheet(PIECE, 'bear-tear'),
  shadow: sheet(PIECE, 'bear-shadow'),
  claws: sheet(PIECE, 'bear-claws'),
  keeper: sheet(PIECE, 'bear-keeper'),
  arm: sheet(PIECE, 'bear-arm', { still: true }),
  paw: sheet(PIECE, 'bear-paw'),
  pawR: sheet(PIECE, 'bear-paw-right'),
  rake: sheet(PIECE, 'bear-rake'),
  rakeR: sheet(PIECE, 'bear-rake-right'),
  marks: sheet(PIECE, 'bear-marks'),
  pad: sheet(PIECE, 'bear-pad', { still: true }),
  spot: sheet(PIECE, 'bear-spot', { still: true }),
  stillhere: sheet(PIECE, 'bear-stillhere'),
};
// Sheet file of each art key (for the test's tag checks)
export const SHEETS = { night: 'bear-night', bear: 'bear-bear', eyes: 'bear-eyes', window: 'bear-window', tear: 'bear-tear', shadow: 'bear-shadow', claws: 'bear-claws', keeper: 'bear-keeper', arm: 'bear-arm', paw: 'bear-paw', pawR: 'bear-paw-right', rake: 'bear-rake', rakeR: 'bear-rake-right', marks: 'bear-marks', pad: 'bear-pad', spot: 'bear-spot', stillhere: 'bear-stillhere' };

// The anchors bear_game.lua wrote (bear-game-anchors.json); the test checks these are the same.
export const ANCHORS = {
  layout: { win: [10, 96], box: [14, 108, 152, 88], bear: [42, 34], pad: 220, marks: [[16, 98], [25, 98], [34, 98]] },
  eyes: { 'rise-1': [36, 49], 'rise-2': [36, 35], idle: [36, 23], 'left-up': [36, 24], 'right-up': [36, 24], 'both-up': [36, 25], roar: [36, 20], 'slump-yawn': [36, 28] },
  keeper: { centre: [6, 7] },
  stillhere: { size: [100, 74] },
};
export const STEPS = 105, MARKS = 3, MERCY = 6, LAST = 3; // LAST: the shadow's dark steps before a slam
const RAISED = 22; // rows of the raised paw shown over the title bar (its pad, toes and claws)
const TILE = { shadow: [24, 8], arm: [24, 8], claws: [24, 6], paw: [24, 48], rake: [48, 28] };
const [WX, WY] = ANCHORS.layout.win;
const [OX, OY, BW, BH] = ANCHORS.layout.box;
const PADY = ANCHORS.layout.pad;

// Where a paw hangs over a rect (box x of its centre): the middle of a narrow one; the inner edge of a wide one that runs to
// a wall (so a double's two paws hang over the gap they leave); both ends of one that spans the box (the rake).
export function pawAnchors(r) {
  const w = r.x1 - r.x0;
  if (w <= 48) return [Math.round((r.x0 + r.x1) / 2)];
  if (r.x0 <= 0 && r.x1 >= BW) return [24, BW - 24];
  if (r.x0 <= 0) return [r.x1 - 12];
  if (r.x1 >= BW) return [r.x0 + 12];
  return [Math.round((r.x0 + r.x1) / 2)];
}
const sideOf = ax => (ax < BW / 2 ? 'left' : 'right');
const isRake = r => r.y1 < BH;

// Tiles of a sheet's frame cut to fill [x0, x1) x [y0, y1) (frame px), the grid anchored at (gx, gy) so a dither runs on.
function tiles(ops, art, frame, [tw, th], x0, x1, y0, y1, gx, gy) {
  for (let ty = gy + Math.floor((y0 - gy) / th) * th; ty < y1; ty += th) {
    for (let tx = gx + Math.floor((x0 - gx) / tw) * tw; tx < x1; tx += tw) {
      const ax = Math.max(tx, x0), ay = Math.max(ty, y0), bx = Math.min(tx + tw, x1), by = Math.min(ty + th, y1);
      if (bx > ax && by > ay) ops.push({ art, frame, x: ax, y: ay, sx: ax - tx, sy: ay - ty, w: bx - ax, h: by - ay });
    }
  }
}
// A row of paws (or rakes) along the bottom of a rect: whole tiles from the left, the last one cut; a narrow rect takes the
// middle of one paw. Its top is cut when the rect is shallower than the paw.
function pawRow(ops, art, frame, [tw, th], x0, x1, bottom, top) {
  const h = Math.min(th, bottom - top), sy = th - h;
  if (x1 - x0 < tw) { const sx = Math.floor((tw - (x1 - x0)) / 2); ops.push({ art, frame, x: x0, y: bottom - h, sx, sy, w: x1 - x0, h }); return; }
  for (let x = x0; x < x1; x += tw) ops.push({ art, frame, x, y: bottom - h, sx: 0, sy, w: Math.min(tw, x1 - x), h });
}

export const marksOf = o => o.marks ?? MARKS;
export const isOut = o => marksOf(o) <= 0;
export const isWin = o => !isOut(o) && o.t >= STEPS;
const warning = (o, th) => o.t >= th.warnStep && o.t < th.slamStep;
const solid = (o, th) => o.t >= th.slamStep && o.t < th.slamStep + 3;
const ended = o => isOut(o) || o.t >= STEPS;

// The bear's pose and its eyes on this step.
export function bearPose(o, after = 0, tell = null) {
  const shut = { pose: 'slump-yawn', eyes: 'shut' };
  const eyes = busy => tell ?? (busy ? 'narrowed' : 'open');
  if (isOut(o)) return { pose: 'roar', eyes: eyes(true) };
  if (isWin(o)) return after >= 1 ? shut : { pose: 'idle', eyes: eyes(false) };
  if (o.t < 2) return { pose: 'rise-1', eyes: eyes(false) };
  if (o.t < 4) return { pose: 'rise-2', eyes: eyes(false) };
  const hit = o.hitStep ?? -1;
  if (hit >= 0 && o.t - hit <= 3) return { pose: 'roar', eyes: eyes(true) };
  const sides = new Set();
  for (const th of o.threats) if (o.t >= th.warnStep && th.slamStep - o.t > LAST) for (const r of th.rects) for (const a of pawAnchors(r)) sides.add(sideOf(a));
  const busy = o.threats.some(th => warning(o, th) || solid(o, th));
  const pose = sides.size === 2 ? 'both-up' : sides.has('left') ? 'left-up' : sides.has('right') ? 'right-up' : 'idle';
  return { pose, eyes: eyes(busy) };
}

// The doorkeeper's frame, or null on a mercy flicker's hidden step. prev: the last step's { t, x, y } (to show a step).
export function keeperPose(o, { after = 0, prev = null, reduced = false } = {}) {
  if (isOut(o)) return 'flat';
  if (isWin(o) && after >= 1) return 'relief';
  const hit = o.hitStep ?? -1, since = o.t - hit;
  if (hit >= 0 && since >= 1 && since <= MERCY) return reduced || since % 2 === 1 ? 'hit' : null;
  if (prev && prev.t === o.t - 1) {
    const dx = o.me.x - prev.x, dy = o.me.y - prev.y;
    if (dx < 0) return 'step-left';
    if (dx > 0) return 'step-right';
    if (dy) return o.t & 1 ? 'step-left' : 'step-right';
  }
  return 'stand';
}

// The whole frame as a list of copies, bottom first: { art, frame, x, y } plus { sx, sy, w, h } for a cut tile.
export function bearScene(o, { reduced = false, after = 0, prev = null, tell = null } = {}) {
  const ops = [], put = (art, frame, x, y) => ops.push({ art, frame, x: Math.round(x), y: Math.round(y) });
  const t = o.t, tellEyes = tell ?? o.tell ?? null;
  // once the game is over the paws' own clock runs on with `after` (they lift, the claw marks fade); no new warning shows
  const over = ended(o), at = over ? t + after : t;
  // and only the paws that had landed before the end play on (a paw still in its warning never comes down)
  if (over) o = { ...o, threats: o.threats.filter(th => th.slamStep < t) };
  put('night', 0, 0, 0);
  const bp = bearPose(o, after, tellEyes);
  const [bx, by] = ANCHORS.layout.bear, ea = ANCHORS.eyes[bp.pose];
  put('bear', [bp.pose, 0], bx, by);
  put('eyes', [bp.eyes, 0], bx + ea[0], by + ea[1]);
  put('window', 0, WX, WY);
  // the tears in the title bar where paws came through (from the top of the box), kept to the end
  for (const th of o.threats) if (t >= th.slamStep) for (const r of th.rects) if (r.y0 <= 0) for (const a of pawAnchors(r)) put('tear', [t === th.slamStep ? 'rip' : 'torn', 0], OX + a - 12, WY);
  // the warning shadows: exactly the rects, darker for the last 3 steps (none once the game is over)
  if (!over) for (const th of o.threats) if (warning(o, th)) for (const r of th.rects) tiles(ops, 'shadow', [th.slamStep - t <= LAST ? 'dark' : 'light', 0], TILE.shadow, OX + r.x0, OX + r.x1, OY + r.y0, OY + r.y1, OX, OY);
  // claw marks on the floor after a paw that reached it: fresh, fading, gone (4 steps each)
  for (const th of o.threats) {
    const k = at - th.slamStep - 3;
    if (k < 0 || k >= 12) continue;
    for (const r of th.rects) if (!isRake(r)) tiles(ops, 'claws', [['fresh', 'fading', 'gone'][k >> 2], 0], TILE.claws, OX + r.x0, OX + r.x1, OY + BH - 6, OY + BH, OX + r.x0, OY + BH - 6);
  }
  // a lifting paw (not solid any more): its paw row, rising
  for (const th of o.threats) {
    const k = at - th.slamStep - 3;
    if (k < 0 || k > 1) continue;
    for (const r of th.rects) {
      const rake = isRake(r), left = (r.x0 + r.x1) / 2 < BW / 2, art = rake ? (left ? 'rake' : 'rakeR') : left ? 'paw' : 'pawR';
      pawRow(ops, art, ['lift', 0], rake ? TILE.rake : TILE.paw, OX + r.x0, OX + r.x1, OY + r.y1 - 6 * (k + 1), OY + r.y0);
    }
  }
  // the doorkeeper (out: drawn again over the paws once they lift, so he lies flat in plain sight)
  const kp = keeperPose(o, { after, prev, reduced });
  const keeper = () => { if (kp) put('keeper', [kp, 0], OX + o.me.x - ANCHORS.keeper.centre[0], OY + o.me.y - ANCHORS.keeper.centre[1] + (kp === 'flat' ? 1 : 0)); };
  keeper();
  // solid paws: the forearm down through the title bar, the paws (or rakes) along the bottom: exactly the rect
  for (const th of o.threats) {
    if (!(at >= th.slamStep && at < th.slamStep + 3)) continue;
    const frame = at === th.slamStep ? 'smear' : 'slam';
    for (const r of th.rects) {
      const rake = isRake(r), left = (r.x0 + r.x1) / 2 < BW / 2;
      const [tw, tht] = rake ? TILE.rake : TILE.paw, bottom = OY + r.y1, top = r.y0 <= 0 ? WY : OY + r.y0;
      if (bottom - tht > top) tiles(ops, 'arm', 0, TILE.arm, OX + r.x0, OX + r.x1, top, bottom - tht, OX, OY);
      pawRow(ops, rake ? (left ? 'rake' : 'rakeR') : left ? 'paw' : 'pawR', rake ? ['rake', 0] : [frame, 0], [tw, tht], OX + r.x0, OX + r.x1, bottom, top);
    }
  }
  // the warning's last 3 steps: the paw that the bear held up comes down onto the title bar over its rect (the paw and
  // claws only, so the bear's face stays clear); before that the raised arm is the bear's own frame (left-up, right-up, both-up)
  if (isOut(o) && after >= 1) keeper();
  for (const th of o.threats) {
    if (over || !warning(o, th) || th.slamStep - t > LAST) continue;
    for (const r of th.rects) for (const a of pawAnchors(r)) ops.push({ art: sideOf(a) === 'left' ? 'paw' : 'pawR', frame: ['raised', 0], x: OX + a - 12, y: WY + 4 - RAISED, sx: 0, sy: TILE.paw[1] - RAISED, w: TILE.paw[0], h: RAISED });
  }
  // the three marks in the title bar: the lost ones scratched through, from the left
  const lost = MARKS - Math.max(0, marksOf(o));
  ANCHORS.layout.marks.forEach(([x, y], i) => put('marks', [i < lost ? 'used' : 'whole', 0], x, y));
  // the touch pad, and the doorkeeper's spot on it (the pad is the box in small)
  put('pad', 0, 0, PADY);
  put('spot', 0, Math.round((o.me.x * 179) / 151) - 3, PADY + Math.round((o.me.y * 79) / 87) - 3);
  if (isWin(o)) { const [sw, sh] = ANCHORS.stillhere.size; put('stillhere', [after < 1 ? 'stamp-in' : 'settle', 0], OX + BW / 2 - sw / 2, OY + BH / 2 - sh / 2); }
  return ops;
}

const last = new WeakMap(); // per canvas: the doorkeeper's last drawn step, so a move shows as a step
export function drawBear(g, o, { reduced = false, after = 0, tell = null } = {}) {
  g.clearRect?.(0, 0, 180, 300);
  const seen = last.get(g) ?? null;
  const prev = seen && seen.t === o.t ? seen.prev : seen; // a repaint of the same step keeps the step before it
  if (!seen || seen.t !== o.t) last.set(g, { t: o.t, x: o.me.x, y: o.me.y, prev: seen && { t: seen.t, x: seen.x, y: seen.y } });
  const ops = bearScene(o, { reduced, after, prev, tell });
  for (const op of ops) {
    const s = ART[op.art];
    const i = Array.isArray(op.frame) ? frameOf(s, op.frame[0], op.frame[1]) : op.frame;
    if (op.w !== undefined) blitPart(g, s, i, op.sx, op.sy, op.w, op.h, op.x, op.y);
    else blit(g, s, i, op.x, op.y);
  }
  return ops;
}
